import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'

import {
  focusHandoff,
  sideScrollState,
  sideScrollStep,
  type SideScrollSide,
  type SideScrollState,
} from './sideScroll'

const NONE: SideScrollState = { canScrollLeft: false, canScrollRight: false }

/** Watches a scroll box and reports which sides still hide content. Re-measures
    on scroll, when the box resizes with the window, and when the table inside
    it resizes with its rows (loading skeleton vs. loaded page, long ids). */
function useSideScrollState(scrollRef: React.RefObject<HTMLDivElement>): SideScrollState {
  const [state, setState] = useState<SideScrollState>(NONE)
  useEffect(() => {
    const box = scrollRef.current
    if (box === null) return
    const measure = (): void => {
      const next = sideScrollState(box)
      setState((current) =>
        current.canScrollLeft === next.canScrollLeft &&
        current.canScrollRight === next.canScrollRight
          ? current
          : next,
      )
    }
    measure()
    box.addEventListener('scroll', measure, { passive: true })
    const observer = new ResizeObserver(measure)
    observer.observe(box)
    if (box.firstElementChild !== null) observer.observe(box.firstElementChild)
    return () => {
      box.removeEventListener('scroll', measure)
      observer.disconnect()
    }
  }, [scrollRef])
  return state
}

interface ScrollArrowProps {
  side: SideScrollSide
  box: React.RefObject<HTMLDivElement>
  buttonRef: React.RefObject<HTMLButtonElement>
  onFocusChange: (side: SideScrollSide, focused: boolean) => void
}

/** One floating button over a corner of the header row. */
function ScrollArrow({ side, box, buttonRef, onFocusChange }: ScrollArrowProps): React.JSX.Element {
  const direction = side === 'left' ? -1 : 1
  return (
    <Button
      ref={buttonRef}
      type="button"
      variant="outline"
      size="icon"
      aria-label={`Scroll table ${side}`}
      title={`Scroll ${side}`}
      className={`pointer-events-auto absolute top-1.5 h-7 w-7 ${side === 'left' ? 'left-2' : 'right-2'}`}
      onFocus={() => {
        onFocusChange(side, true)
      }}
      // A button removed while focused fires no blur, so a blur that does
      // arrive is a real one: the focus ring moved somewhere else on the page.
      onBlur={(event) => {
        if (event.currentTarget.isConnected) onFocusChange(side, false)
      }}
      onClick={() => {
        const el = box.current
        if (el === null) return
        el.scrollBy({ left: direction * sideScrollStep(el.clientWidth), behavior: 'smooth' })
      }}
    >
      {side === 'left' ? <ChevronLeft aria-hidden="true" /> : <ChevronRight aria-hidden="true" />}
    </Button>
  )
}

interface SideScrollArrowsProps {
  /** The scroll box the arrows drive — the `containerRef` of a `<Table>`. */
  scrollRef: React.RefObject<HTMLDivElement>
}

/**
 * Floating arrows that scroll a wide table sideways (TRA-1166). A mouse
 * wheel only scrolls up and down, so without these a mouse user has no way
 * to reach the columns past the right edge — touchpad users swipe. The
 * arrows show only while something is hidden on that side, and the strip
 * they sit in is sticky, so they stay in view while scrolling down the rows.
 *
 * Render it right before the `<Table>` inside a `relative` wrapper.
 */
export function SideScrollArrows({ scrollRef }: SideScrollArrowsProps): React.JSX.Element | null {
  const { canScrollLeft, canScrollRight } = useSideScrollState(scrollRef)
  const leftRef = useRef<HTMLButtonElement>(null)
  const rightRef = useRef<HTMLButtonElement>(null)
  // Which arrow holds the focus ring. Still reads `right` below after the
  // right arrow has gone, which is what makes the hand-off possible.
  const focusedSide = useRef<SideScrollSide | null>(null)

  useLayoutEffect(() => {
    const next = focusHandoff(focusedSide.current, { canScrollLeft, canScrollRight })
    if (next.kind === 'keep') return
    if (next.kind === 'clear') {
      focusedSide.current = null
      return
    }
    const target = next.to === 'left' ? leftRef.current : rightRef.current
    focusedSide.current = target === null ? null : next.to
    target?.focus()
  }, [canScrollLeft, canScrollRight])

  if (!canScrollLeft && !canScrollRight) return null
  const onFocusChange = (side: SideScrollSide, focused: boolean): void => {
    if (focused) focusedSide.current = side
    else if (focusedSide.current === side) focusedSide.current = null
  }
  // Zero height, so the strip takes no room above the header; the buttons
  // float over the header row's corners and are the only clickable part.
  return (
    <div className="pointer-events-none sticky top-2 z-10 h-0">
      {canScrollLeft && (
        <ScrollArrow side="left" box={scrollRef} buttonRef={leftRef} onFocusChange={onFocusChange} />
      )}
      {canScrollRight && (
        <ScrollArrow
          side="right"
          box={scrollRef}
          buttonRef={rightRef}
          onFocusChange={onFocusChange}
        />
      )}
    </div>
  )
}
